import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { searchCategory } from "../../redux/reducers/Properties/PropertiesSlice";
import { setSearchData } from "../../redux/reducers/Search/SearchSlice";
import { useNavigate } from "react-router-dom";
import API from "../../utils/helpers/APIHelper";
import { ENDPOINTS } from "../../API/apiConstants";

interface LocationInputSimpleProps {
  value: string;
  setValue: (val: string) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  propertyTypes?: string[];
  priceRange?: [number, number];
  guestCount?: number;
  resetForm?: () => void;
  onSearchComplete?: () => void;
  initialLat?: number | null;
  initialLng?: number | null;
  initialLocationData?: any;
}

interface NeighborhoodOption {
  neighborhood: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
}

const LocationInputSimple: React.FC<LocationInputSimpleProps> = ({
  value,
  setValue,
  startDate,
  endDate,
  propertyTypes = [],
  priceRange = [0, 5000],
  guestCount = 1,
  onSearchComplete,
  initialLat,
  initialLng,
  initialLocationData,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<NeighborhoodOption[]>([]);
  const [filtered, setFiltered] = useState<NeighborhoodOption[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<NeighborhoodOption | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const storedSearchData = useSelector((state: RootState) => state.search.searchedData);
  const propertyTypesFromDB = useSelector((state: RootState) => state.property.propertyTypes);

  // ✅ Fetch neighborhoods from DB on mount
  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const data: any = await API.get(ENDPOINTS.NEIGHBORHOODS);
        if (data?.success) {
          setNeighborhoods(data.neighborhoods);
          setFiltered(data.neighborhoods);
          console.log("✅ Neighborhoods loaded:", data.neighborhoods);
        }
      } catch (err) {
        console.error("❌ Failed to fetch neighborhoods:", err);
      }
    };
    fetchNeighborhoods();
  }, []);

  // ✅ Load initial location from Redux if available
  useEffect(() => {
    if (initialLat && initialLng && initialLocationData && !selectedLocation) {
      setSelectedLocation({
        ...initialLocationData,
        lat: initialLat,
        lng: initialLng,
      });
      const label = [
        initialLocationData.neighborhood,
        initialLocationData.city,
        initialLocationData.country,
      ]
        .filter(Boolean)
        .join(", ");
      if (!value) setValue(label);
    }
  }, [initialLat, initialLng, initialLocationData]);

  // ✅ Filter as user types
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setValue(input);
    setSelectedLocation(null); // clear selection when typing
    setOpen(true);

    if (input.trim() === "") {
      setFiltered(neighborhoods);
    } else {
      const matches = neighborhoods.filter(
        (n) =>
          n.neighborhood.toLowerCase().includes(input.toLowerCase()) ||
          n.city.toLowerCase().includes(input.toLowerCase())
      );
      setFiltered(matches);
    }
  };

  // ✅ User picks a neighborhood
  const handleSelect = (option: NeighborhoodOption) => {
    setSelectedLocation(option);
    setValue(`${option.neighborhood}, ${option.city}, ${option.country}`);
    setOpen(false);
  };

  // ✅ Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPropertyTypeIds = (typeNames: string[]): string[] => {
    if (!propertyTypesFromDB || propertyTypesFromDB.length === 0) return [];
    return typeNames
      .map((name) => {
        const pt = propertyTypesFromDB.find(
          (p: any) => p.name.toLowerCase() === name.toLowerCase()
        );
        return pt ? String(pt.id) : null;
      })
      .filter(Boolean) as string[];
  };

  const handleSearch = async () => {
    if (!selectedLocation?.lat || !selectedLocation?.lng) {
      alert("Please select a neighborhood from the list");
      return;
    }

    console.log("🔍 Searching with:", selectedLocation);

    const propertyTypeIds = getPropertyTypeIds(propertyTypes);
    const propertyTypeIdsString = propertyTypeIds.join(",");

    try {
      const params = new URLSearchParams();

      if (propertyTypeIds.length > 0) {
        propertyTypeIds.forEach((id) => params.append("q[property_type_ids][]", id));
      }

      // ✅ Always exact neighborhood match — guaranteed to work
      params.append("q[neighborhood]", selectedLocation.neighborhood);
      params.append("q[latitude]", selectedLocation.lat.toString());
      params.append("q[longitude]", selectedLocation.lng.toString());
      params.append("q[radius]", "5");

      if (priceRange[0] > 0) params.append("q[min_price]", priceRange[0].toString());
      if (priceRange[1] < 5000) params.append("q[max_price]", priceRange[1].toString());
      if (guestCount > 1) params.append("q[guest_count]", guestCount.toString());
      if (startDate) params.append("q[start_date]", startDate.toISOString().split("T")[0]);
      if (endDate) params.append("q[end_date]", endDate.toISOString().split("T")[0]);

      params.append("page", "1");

      const searchQuery = params.toString();
      console.log("🔍 SEARCH QUERY:", searchQuery);

      const action = await dispatch(searchCategory(searchQuery));

      if (action.payload?.success) {
        const locationLabel = [
          selectedLocation.neighborhood,
          selectedLocation.city,
          selectedLocation.country,
        ]
          .filter(Boolean)
          .join(", ");

        dispatch(
          setSearchData({
            ...storedSearchData,
            startDate,
            endDate,
            latitude: selectedLocation.lat,
            longitude: selectedLocation.lng,
            locationData: selectedLocation,
            label: locationLabel,
            searchedResult: action.payload,
            propertyTypeId: propertyTypeIdsString,
            priceMin: priceRange[0],
            priceMax: priceRange[1],
            guestCount,
          })
        );

        navigate("/listing-stay-map");
        if (onSearchComplete) onSearchComplete();
      } else {
        alert("No properties found. Try a different neighborhood.");
      }
    } catch (err) {
      console.error("❌ Search error:", err);
      alert("Search failed. Please try again.");
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative flex items-center">
        <Search className="absolute left-4 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          placeholder="Search for a neighborhood"
          className="w-full pl-12 pr-28 h-14 rounded-full border border-gray-200 shadow-lg text-md md:text-lg focus:ring-0 focus:border-gray-900 outline-none"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="absolute right-2 bg-[#F56948] hover:bg-[#e2583d] text-white rounded-full px-5 py-2 font-medium transition"
        >
          Search
        </button>
      </div>

      {/* ✅ Dropdown */}
      {open && filtered.length > 0 && (
        <div className="absolute mt-2 w-full bg-white rounded-2xl shadow-xl z-50 p-4 max-h-64 overflow-y-auto">
          <h4 className="font-semibold text-sm text-gray-500 mb-3">
            Available Neighborhoods
          </h4>
          <div className="flex flex-col gap-1">
            {filtered.map((option) => (
              <button
                key={`${option.neighborhood}-${option.city}`}
                onClick={() => handleSelect(option)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-left hover:bg-gray-50 transition w-full"
              >
                <Search className="w-4 h-4 text-[#F56948] flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-800">
                    {option.neighborhood}
                  </span>
                  <span className="text-gray-400 ml-1">
                    · {option.city}, {option.country}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ✅ No results */}
      {open && value.trim() !== "" && filtered.length === 0 && (
        <div className="absolute mt-2 w-full bg-white rounded-2xl shadow-xl z-50 p-4">
          <p className="text-sm text-gray-500 text-center">
            No neighborhoods found for "{value}"
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationInputSimple;